import React, { useEffect, useState } from 'react';

/**
 * <SectionIndex> — fixed bottom-left cinematic "chapter" indicator.
 *
 * Updates via IntersectionObserver on every top-level <section> with
 * an `id` attribute. Shows e.g. `02 / 08 — ABOUT` as the user scrolls,
 * tying the page into a single story.
 *
 * Zero scroll listeners. Zero RAF. Only fires on viewport transitions.
 * Hidden under `lg` so it never crowds mobile.
 */
const LABELS = {
  home: 'HERO',
  about: 'ABOUT',
  classes: 'CLASSES',
  trainers: 'TRAINERS',
  app: 'MEMBER APP',
  memberships: 'MEMBERSHIPS',
  'kid-care': 'KID CARE',
  reviews: 'REVIEWS',
  contact: 'CONTACT',
};

export default function SectionIndex() {
  const [info, setInfo] = useState({ idx: 1, total: 0, label: 'HERO' });

  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Defer all DOM observation until the browser is idle — keeps it out of
    // the critical first-paint path (saves ~150–300 ms on FCP). The 1000ms
    // timeout guarantees it fires even if the page never goes fully idle.
    const idle = window.requestIdleCallback
      ? (cb) => window.requestIdleCallback(cb, { timeout: 1000 })
      : (cb) => setTimeout(cb, 250);
    let io;

    const handle = idle(() => {
      const knownIds = Object.keys(LABELS);
      const sections = knownIds
        .map((id) => document.getElementById(id))
        .filter(Boolean);
      if (!sections.length) return;

      setInfo((s) => ({ ...s, total: sections.length }));

      const visibleMap = new Map();
      io = new IntersectionObserver(
        (entries) => {
          entries.forEach((e) => visibleMap.set(e.target, e.intersectionRatio));
          let best = null;
          let bestRatio = 0;
          for (const [el, ratio] of visibleMap.entries()) {
            if (ratio > bestRatio) {
              bestRatio = ratio;
              best = el;
            }
          }
          if (best) {
            const idx = sections.indexOf(best) + 1;
            setInfo({ idx, total: sections.length, label: LABELS[best.id] || best.id.toUpperCase() });
          }
        },
        { threshold: [0.12, 0.3, 0.55, 0.8] },
      );
      sections.forEach((el) => io.observe(el));
    });

    return () => {
      if (window.cancelIdleCallback && typeof handle === 'number') {
        window.cancelIdleCallback(handle);
      }
      if (io) io.disconnect();
    };
  }, []);

  if (!info.total) return null;

  const pad = (n) => String(n).padStart(2, '0');
  const pct = (info.idx / info.total) * 100;

  return (
    <div
      data-testid="section-index"
      className="hidden lg:flex fixed bottom-6 left-6 z-40 flex-col items-start gap-2 pointer-events-none select-none"
    >
      <div className="flex items-baseline gap-2 font-display tracking-[0.25em] text-white/85">
        <span className="text-xs text-red-500">{pad(info.idx)}</span>
        <span className="text-[10px] text-white/40">/ {pad(info.total)}</span>
      </div>
      <div className="text-[10px] tracking-[0.45em] text-white/55 font-bold">{info.label}</div>
      <div className="h-[1.5px] w-32 bg-white/10 overflow-hidden rounded-full">
        <div
          className="h-full bg-gradient-to-r from-red-600 to-red-400"
          style={{
            width: `${pct}%`,
            transition: 'width 0.6s cubic-bezier(0.22, 1, 0.36, 1)',
            boxShadow: '0 0 10px rgba(239,43,43,0.55)',
          }}
        />
      </div>
    </div>
  );
}
